import { useState, useEffect } from "react"
import { FC } from "react"
import { GenericUpdateDTO } from "../../../models/user"
import UserService from "../../../services/user"
import useNotyf from "../../../context/useNotify"
import { useUser } from "../../../context/useUser"
import HelperService from "../../../services/helper"
import imagePackService from "../../../services/imagePack"
import { ImagePack, Image } from "../../../models/imagePack"
import LoadingSpinner from "../../common/LoadingSpinner"

interface UpdateProfileImageModalProps {
  onClose: () => void
}

const UpdateProfileImageModal: FC<UpdateProfileImageModalProps> = ({ onClose }) => {
  const { user, setUser } = useUser()
  const notyf = useNotyf()

  // data
  const [images, setImages] = useState<ImagePack[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    fetchImages()
  }, [])

  // api
  const fetchImages = async () => {
    try {
      const fetchedImages = await imagePackService.list()
      setImages(fetchedImages)
    } catch (err) {
      console.error("Error fetching images: ", err)
      notyf.error("Failed to load images. Please try again.")
    }
  }

  const updateUser = async (image: Image) => {
    if (!user) return

    setIsLoading(true)
    try {
      const updateObj = { profileImage: image.url }
      const dto: GenericUpdateDTO = { id: user._id, updateObj }

      await UserService.genericUpdate(dto)
      setUser({ ...user, profileImage: image.url })
      notyf.success("Profile image updated.")
      onClose()
    } catch (err) {
      HelperService.showNotfyErr(notyf, err, "Error updating image.")
    } finally {
      setIsLoading(false)
    }
  }

  // methods
  const handleImageClick = (image: Image) => {
    if (!isLoading) {
      updateUser(image)
    }
  }

  const renderImagePack = (pack: ImagePack) => (
    <div key={pack.name} className='mb-4'>
      <h5 className='text-white mb-3'>{pack.name}</h5>
      <div className='row g-3'>
        {pack.images.map((image) => (
          <div key={image.url} className='col-6 col-sm-4 col-md-3'>
            <div
              className={`position-relative d-flex justify-content-center align-items-center flex-column`}
              style={{ cursor: "pointer" }}
              onClick={() => handleImageClick(image)}
            >
              <img
                src={image.url}
                alt={image.title}
                className='img-fluid rounded'
                style={{ opacity: isLoading ? 0.5 : 1 }}
              />
              {pack.name === "Trophy" && image.label && (
                <div className='text-center mt-1 small text-white heading'>{image.label}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <>
      {images && images.length > 0 ? (
        <div className='container-fluid p-4'>
          <h4 className='text-white mb-4'>Select Profile Image</h4>
          {images.map(renderImagePack)}
        </div>
      ) : (
        <div
          className='container-fluid p-5 d-flex justify-content-center align-items-center'
          style={{ minWidth: "250px", width: "500px" }}
        >
          <LoadingSpinner />
        </div>
      )}
    </>
  )
}

export default UpdateProfileImageModal
